import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { HttpService } from '../http/http.service';
import { AuthenticationService } from '../authentication/authentication.service';
import { environment } from '../../../../environments/environment';

export interface QuestionTemplate {
  id: string;
  name: string;
  is_default: boolean;
  created_at: string;
}

export interface GenerateQuestionsResponse {
  questions: { question_text: string }[];
}

@Injectable({
  providedIn: 'root'
})
export class QuestionTemplateService {

  constructor(
    private readonly _http: HttpService,
    private readonly _auth: AuthenticationService
  ) {}

  /** Fetch all templates for the current user, ordered oldest-first */
  getTemplates(): Observable<QuestionTemplate[]> {
    return this._http.get(
      'rest/v1/question_templates?select=id,name,is_default,created_at&order=created_at.asc'
    );
  }

  /**
   * Create a new template.
   * The Prefer header makes Supabase return the created row so we get the new id.
   * user_id must be supplied explicitly so the RLS WITH CHECK (user_id = auth.uid()) passes.
   */
  createTemplate(name: string, isDefault = false): Observable<QuestionTemplate[]> {
    const userId = this._getUserId();
    return this._http.post(
      'rest/v1/question_templates',
      { name, is_default: isDefault, user_id: userId },
      false,
      { headers: { 'Prefer': 'return=representation' } }
    );
  }

  private _getUserId(): string | null {
    const userDetail = this._auth.getUserDetail();
    return userDetail?.user?.id || (userDetail as any)?.id || null;
  }

  /** Rename an existing template */
  renameTemplate(id: string, name: string): Observable<any> {
    return this._http.patch(
      `rest/v1/question_templates?id=eq.${id}`,
      { name },
      false
    );
  }

  /** Delete a template (questions cascade automatically) */
  deleteTemplate(id: string): Observable<any> {
    return this._http.delete(`rest/v1/question_templates?id=eq.${id}`, false);
  }

  /**
   * AI Question Generator.
   *
   * PLACEHOLDER — returns mock data after a 1.5 s delay.
   * When your Edge Function is ready, replace the return statement with:
   *
   *   return this._http.post<GenerateQuestionsResponse>(
   *     `${environment.apiUrl}/functions/v1/generate-questions`,
   *     { prompt },
   *     false
   *   );
   *
   * Expected API contract:
   *   POST /functions/v1/generate-questions
   *   Body:    { "prompt": "Generate BANT questions for enterprise SaaS" }
   *   Response: { "questions": [{ "question_text": "..." }, ...] }
   */
  generateQuestions(prompt: string): Observable<GenerateQuestionsResponse> {
    // --- PLACEHOLDER START ---
    const mock: GenerateQuestionsResponse = {
      questions: [
        { question_text: 'What are the top challenges you are trying to address?' },
        { question_text: 'What is the business impact of not solving this problem?' },
        { question_text: 'Who are the key decision-makers in this process?' },
        { question_text: 'What is your budget range for this solution?' },
        { question_text: 'What is your implementation timeline?' },
        { question_text: 'What solutions have you tried before and why did they fall short?' },
        { question_text: 'How are you currently measuring success in this area?' },
        { question_text: 'What does an ideal outcome look like for you?' },
      ]
    };
    return of(mock).pipe(delay(1500));
    // --- PLACEHOLDER END ---

    // TODO: uncomment when Edge Function is live:
    // return this._http.post(
    //   `${environment.apiUrl}/functions/v1/generate-questions`,
    //   { prompt },
    //   false
    // );
  }
}
